import { getConfig } from '../config';

export type GetAccessToken = (resource: string) => Promise<string | undefined>;

export interface Operator {
  id: string;
  slug: string;
  name: string;
  active: boolean;
}

export interface Tour {
  id: string;
  tenantId: string;
  title: string;
  bokunProductId: string | null;
  basePriceEur: number;
  publicTitlePt: string | null;
  publicTitleEn: string | null;
  publicDescriptionPt: string | null;
  publicDescriptionEn: string | null;
  published: boolean;
}

export interface TourPublicContent {
  publicTitlePt: string | null;
  publicTitleEn: string | null;
  publicDescriptionPt: string | null;
  publicDescriptionEn: string | null;
  published: boolean;
}

export interface CustomerRef {
  id: string;
  displayName: string;
}

export interface Booking {
  id: string;
  tourId: string;
  startsAt: string;
  channel: string;
  pax: number;
  customerName: string | null;
  customerPaidEur: number;
  operatorNetEur: number;
  platformNetEur: number;
  status: string;
  customer: CustomerRef | null;
}

export interface CustomerIdentity {
  kind: string;
  value: string;
}

export interface CustomerListItem {
  id: string;
  displayName: string;
  identities: CustomerIdentity[];
  marketingConsent: string | null;
  bookingsCount: number;
  lifetimeValueEur: number;
  lastBookingAt: string | null;
}

export interface CustomerSummary {
  id: string;
  displayName: string;
  identities: CustomerIdentity[];
}

export interface ConsentRecordView {
  id: string;
  state: string;
  mechanism: string;
  jurisdiction: string;
  recordedAt: string;
  proof: string | null;
}

export interface ErasureRequestView {
  id: string;
  requestedAt: string;
  dueBy: string;
  status: string;
}

export interface CustomerBookingView {
  id: string;
  tourId: string;
  startsAt: string;
  channel: string;
  pax: number;
  customerPaidEur: number;
  status: string;
}

export interface CustomerDetail {
  id: string;
  displayName: string;
  identities: CustomerIdentity[];
  bookings: CustomerBookingView[];
  consentHistory: ConsentRecordView[];
  erasureRequests: ErasureRequestView[];
}

export interface ChannelSplit {
  customerPays: number;
  operatorNet: number;
  platformNet: number;
  otaCommission: number;
}

export interface ReportRow {
  channel: string;
  bookings: number;
  pax: number;
  customerPaid: number;
  operatorNet: number;
  platformNet: number;
}

export interface AvailabilitySlotView {
  tourId: string;
  startsAt: string;
  capacity: number;
  booked: number;
  remaining: number;
}

export interface ManifestPassenger {
  bookingId: string;
  customerName: string | null;
  pax: number;
  phone: string | null;
  channel: string;
  status: string;
}

export interface ManifestDeparture {
  tourId: string;
  tourTitle: string;
  startsAt: string;
  capacity: number | null;
  seatsTaken: number;
  seatsFree: number | null;
  bookings: ManifestPassenger[];
}

export interface ManifestResponse {
  date: string;
  departures: ManifestDeparture[];
}

export interface MonthReportRow {
  channel: string;
  bookings: number;
  pax: number;
  customerPaid: number;
  operatorNet: number;
  platformNet: number;
  distinctCustomers: number;
}

export interface MonthTotals {
  bookings: number;
  pax: number;
  customerPaid: number;
  operatorNet: number;
  platformNet: number;
  distinctCustomers: number;
}

export interface MonthReportResponse {
  month: string;
  rows: MonthReportRow[];
  totals: MonthTotals;
  previous: { month: string; totals: MonthTotals };
}

export interface PendingRequest {
  id: string;
  tourTitle: string;
  startsAt: string;
  pax: number;
  customerName: string | null;
  email: string | null;
  phone: string | null;
  // True when the request linked to a customer that already existed; the contacts above are still what the
  // requester typed, not the matched customer's stored identity.
  matchedExistingCustomer: boolean;
  channel: string;
  language: string | null;
  createdAt: string;
}

/** Extracts a human message from an RFC-7807 ProblemDetails body, falling back to the status line. */
export async function extractError(resp: Response, method: string, path: string): Promise<string> {
  try {
    const body: unknown = await resp.json();
    if (body && typeof body === 'object') {
      const pd = body as { detail?: string; title?: string };
      if (pd.detail) return pd.detail;
      if (pd.title) return pd.title;
    }
  } catch {
    // non-JSON error body — fall through to the status line
  }
  return `${method} ${path} → ${resp.status}`;
}

export function createApiClient(getToken: GetAccessToken) {
  const cfg = getConfig();
  const base = cfg.apiUrl.replace(/\/$/, '');

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await getToken(cfg.apiUrl);
    const headers = new Headers(init.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    headers.set('Accept', 'application/json');

    const resp = await fetch(`${base}${path}`, { ...init, headers });
    if (!resp.ok) {
      throw new Error(await extractError(resp, init.method ?? 'GET', path));
    }
    return (await resp.json()) as T;
  }

  function post<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' }
    });
  }

  async function postVoid(path: string): Promise<void> {
    const token = await getToken(cfg.apiUrl);
    const headers = new Headers();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const resp = await fetch(`${base}${path}`, { method: 'POST', headers });
    if (!resp.ok) {
      throw new Error(await extractError(resp, 'POST', path));
    }
  }

  return {
    ping: () => request<{ status: string; env: string; utc: string }>('/api/ping'),
    whoami: () => request<{ sub: string; name: string | null; claims: { type: string; value: string }[] }>('/api/whoami'),
    getOperators: () => request<Operator[]>('/operators'),
    getTours: () => request<Tour[]>('/tours'),
    createTour: (body: { title: string; bokunProductId: string | null; basePriceEur: number }) =>
      post<Tour>('/tours', body),
    updateTourPublicContent: (id: string, body: TourPublicContent) =>
      request<Tour>(`/tours/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' }
      }),
    getBookings: () => request<Booking[]>('/bookings'),
    createBooking: (body: {
      tourId: string;
      startsAt: string;
      channel: string;
      pax: number;
      customerName: string | null;
      customerEmail: string | null;
      customerPhone: string | null;
      customerId: string | null;
      language: string;
    }) => post<{ booking: Booking; split: ChannelSplit }>('/bookings', body),
    getQuote: (tourId: string, channel: string) =>
      request<ChannelSplit>(`/bookings/quote?tourId=${encodeURIComponent(tourId)}&channel=${encodeURIComponent(channel)}`),
    getReport: () => request<ReportRow[]>('/bookings/report'),
    getAvailability: (tourId: string) => request<AvailabilitySlotView[]>(`/tours/${tourId}/availability`),
    createAvailability: (tourId: string, body: { startsAt: string; capacity: number }) =>
      post<{ tourId: string; startsAt: string; capacity: number }>(`/tours/${tourId}/availability`, body),
    getCustomers: (search?: string) =>
      request<CustomerListItem[]>(`/customers${search && search.trim() !== '' ? `?search=${encodeURIComponent(search.trim())}` : ''}`),
    getCustomer: (id: string) => request<CustomerDetail>(`/customers/${encodeURIComponent(id)}`),
    createCustomer: (body: { name: string | null; email: string | null; phone: string | null }) =>
      post<CustomerSummary>('/customers', body),
    appendConsent: (id: string, body: { state: string; mechanism: string; jurisdiction: string; proof: string | null }) =>
      post<ConsentRecordView>(`/customers/${encodeURIComponent(id)}/consent`, body),
    requestErasure: (id: string) => post<ErasureRequestView>(`/customers/${encodeURIComponent(id)}/erasure`, {}),
    getManifest: (date?: string) =>
      request<ManifestResponse>(`/manifest${date ? `?date=${encodeURIComponent(date)}` : ''}`),
    getMonthReport: (month: string) =>
      request<MonthReportResponse>(`/bookings/report?month=${encodeURIComponent(month)}`),
    getPendingRequests: () => request<PendingRequest[]>('/bookings/pending'),
    confirmRequest: (id: string) => postVoid(`/bookings/${encodeURIComponent(id)}/confirm`),
    declineRequest: (id: string) => postVoid(`/bookings/${encodeURIComponent(id)}/decline`)
  };
}
